/**
 * Bee Client Wrapper
 * Provides a singleton Bee client instance configured for the current environment
 *
 * URL Priority:
 * 1. User-configured URL from settings (localStorage)
 * 2. Environment variable (VITE_BEE_URL)
 * 3. Default public gateway
 */

import { Bee } from '@ethersphere/bee-js'
import type { BatchId } from '@ethersphere/bee-js'
import { getSettings, getActiveAccountSubdomain, getAccount } from '../storage'
import { logger } from '../../utils/logger'

// Configuration interface
interface BeeConfig {
  defaultStampId: BatchId | null
}

// Static configuration from environment variables
const config: BeeConfig = {
  defaultStampId: (import.meta.env.VITE_DEFAULT_STAMP_ID as BatchId) || null,
}

// Default URLs
const ENV_BEE_URL = import.meta.env.VITE_BEE_URL as string | undefined
const DEFAULT_BEE_URL = 'https://fairdrop.xyz/api/swarm'

// Singleton Bee instance and its URL
let beeInstance: Bee | null = null
let currentBeeUrl: string | null = null

/**
 * Get the effective Bee URL based on priority:
 * 1. User-configured URL from settings
 * 2. Environment variable
 * 3. Default gateway
 */
export function getEffectiveBeeUrl(): string {
  const settings = getSettings()
  if (settings.beeUrl) {
    return settings.beeUrl
  }
  return ENV_BEE_URL || DEFAULT_BEE_URL
}

/**
 * Get or create the Bee client instance
 * Automatically recreates if the configured URL has changed
 */
export function getBee(): Bee {
  const effectiveUrl = getEffectiveBeeUrl()

  // Recreate instance if URL changed
  if (!beeInstance || currentBeeUrl !== effectiveUrl) {
    beeInstance = new Bee(effectiveUrl)
    currentBeeUrl = effectiveUrl
    logger.debug('[Swarm] Bee client using:', effectiveUrl)
  }

  return beeInstance
}

/**
 * Check if Bee node is accessible
 */
export async function checkConnection(): Promise<boolean> {
  try {
    const bee = getBee()
    await bee.checkConnection()
    return true
  } catch (error) {
    logger.error('[Swarm] Connection check failed:', error)
    return false
  }
}

/**
 * Get the configured Bee URL
 */
export function getBeeUrl(): string {
  return getEffectiveBeeUrl()
}

/**
 * Get the default postage stamp ID
 */
export function getDefaultStampId(): BatchId | null {
  return config.defaultStampId
}

/**
 * Get the effective stamp ID based on active account
 * Priority: Account stamp → Global default
 */
export function getEffectiveStampId(): BatchId | null {
  const activeSubdomain = getActiveAccountSubdomain()
  if (activeSubdomain) {
    const account = getAccount(activeSubdomain)
    if (account?.stampId) {
      return account.stampId as unknown as BatchId
    }
  }
  return getDefaultStampId()
}

/**
 * Set a custom stamp ID (for runtime configuration)
 */
export function setDefaultStampId(stampId: BatchId): void {
  config.defaultStampId = stampId
}

/**
 * Reset the Bee instance (useful for testing or reconfiguration)
 */
export function resetBeeInstance(): void {
  beeInstance = null
}

/**
 * Get the Bee node's overlay address
 * First tries /api/bee-info (production server-side access),
 * then falls back to direct Bee API
 */
export async function getBeeOverlay(): Promise<string | null> {
  // Try /api/bee-info endpoint first (production proxy)
  try {
    logger.debug('[Swarm] Fetching overlay from /api/bee-info...')
    const response = await fetch('/api/bee-info')
    if (response.ok) {
      const data = (await response.json()) as { overlay?: string }
      if (data.overlay) {
        logger.debug('[Swarm] Got overlay via /api/bee-info:', data.overlay.slice(0, 16) + '...')
        return data.overlay
      }
    }
  } catch (error) {
    logger.debug('[Swarm] /api/bee-info failed, trying direct API')
  }

  // Fall back to direct Bee API
  try {
    const bee = getBee()
    const addresses = await bee.getNodeAddresses()
    const overlay = addresses.overlay
    const result = typeof overlay === 'string' ? overlay : (overlay as { toHex: () => string }).toHex()
    logger.debug('[Swarm] Got overlay via direct Bee API:', result.slice(0, 16) + '...')
    return result
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    logger.debug('[Swarm] Direct Bee API failed:', err.message)
  }

  logger.error('[Swarm] Could not get Bee overlay from any source')
  return null
}
